/**
 * Pure helper functions for the Gen AI Insights creation wizard (#970).
 *
 * Kept dependency-free (no MUI/React) so step-navigation, @-mention insertion,
 * and guided-toggle text injection can be unit tested without rendering.
 */
import { MultiTableConfigTable } from '../../../../types/gotypes'
import { normalizeTablePath } from '../../../../utils/multitables'

/** Full job-relative path for a table, matching the `folderPath/filename` convention
 * used across the dashboard editor (MultiTableAnalysisWidgetEditor, TOC lookups). */
export function tableFullPath(t: { folderPath?: string; filename: string }): string {
  return t.folderPath ? `${t.folderPath}/${t.filename}` : t.filename
}

// ---------------------------------------------------------------------------
// Step 2 — guided toggles (bullets/prose, length, audience) auto-inject into
// the instructions text as a single trailing sentence, so the AI prompt stays
// one plain string (no new backend fields / API shape).
// ---------------------------------------------------------------------------

export type GuidedFormat = 'bullets' | 'prose'
export type GuidedLength = 'short' | 'medium' | 'long'
export type GuidedAudience = 'executive' | 'analyst' | 'client'

export interface GuidedToggleState {
  format?: GuidedFormat
  length?: GuidedLength
  audience?: GuidedAudience
}

export const GUIDED_LENGTH_LABELS: Record<GuidedLength, string> = {
  short: 'Short (~100w)',
  medium: 'Medium (~200w)',
  long: 'Long (~400w)',
}

export const GUIDED_AUDIENCE_LABELS: Record<GuidedAudience, string> = {
  executive: 'Executive',
  analyst: 'Analyst',
  client: 'Client',
}

/** Marks the start of the auto-injected guidance sentence so it can be found
 * and replaced without disturbing anything the user typed above it. */
export const GUIDANCE_MARKER = '\n\n_Guidance:'

/** Strip a previously-injected guidance sentence, leaving the user's own text intact. */
export function stripGuidance(instructions: string): string {
  const idx = instructions.indexOf(GUIDANCE_MARKER)
  return idx === -1 ? instructions : instructions.slice(0, idx)
}

const GUIDED_LENGTH_TEXT: Record<GuidedLength, string> = {
  short: '~100 words',
  medium: '~200 words',
  long: '~400 words',
}

const GUIDED_AUDIENCE_TEXT: Record<GuidedAudience, string> = {
  executive: 'an executive audience',
  analyst: 'an analyst audience',
  client: 'a client audience',
}

/** Build the guidance sentence for the current toggle selection. Empty string
 * when nothing is selected. */
export function buildGuidanceSentence(toggles: GuidedToggleState): string {
  const parts: string[] = []
  if (toggles.format === 'bullets') parts.push('Present as bullet points.')
  if (toggles.format === 'prose') parts.push('Write as flowing prose.')
  if (toggles.length) {
    parts.push(`Aim for roughly ${GUIDED_LENGTH_TEXT[toggles.length]} (AI output length varies).`)
  }
  if (toggles.audience) {
    parts.push(`Write for ${GUIDED_AUDIENCE_TEXT[toggles.audience]}.`)
  }
  return parts.join(' ')
}

/** Re-derive the instructions text with the current guided-toggle sentence applied.
 * Idempotent: calling it again with different toggles replaces the previous sentence
 * rather than stacking duplicates. */
export function applyGuidedToggles(instructions: string, toggles: GuidedToggleState): string {
  const base = stripGuidance(instructions)
  const sentence = buildGuidanceSentence(toggles)
  if (!sentence) return base
  return `${base}${GUIDANCE_MARKER} ${sentence}`
}

// ---------------------------------------------------------------------------
// #1047 — re-deriving guided-toggle UI state on reopen. The toggles are
// persisted as structured fields alongside the instructions text (the
// `GuidedToggles` type in gotypes.ts, loose strings since it round-trips
// through the Go widget-data struct); this validates and narrows them back
// to the literal-typed `GuidedToggleState` the toggle buttons use.
// ---------------------------------------------------------------------------

/** Loosely-typed persisted shape (plain strings, as stored on the widget data /
 * mirrored from the Go struct) — narrower than `GuidedToggleState` because
 * anything read back from storage could in principle carry a stale/unknown
 * value and must be validated before it drives the UI. */
export interface PersistedGuidedToggles {
  format?: string
  length?: string
  audience?: string
}

const GUIDED_FORMAT_VALUES: readonly GuidedFormat[] = ['bullets', 'prose']
const GUIDED_LENGTH_VALUES: readonly GuidedLength[] = ['short', 'medium', 'long']
const GUIDED_AUDIENCE_VALUES: readonly GuidedAudience[] = ['executive', 'analyst', 'client']

function isGuidedFormat(v: string | undefined): v is GuidedFormat {
  return GUIDED_FORMAT_VALUES.includes(v as GuidedFormat)
}
function isGuidedLength(v: string | undefined): v is GuidedLength {
  return GUIDED_LENGTH_VALUES.includes(v as GuidedLength)
}
function isGuidedAudience(v: string | undefined): v is GuidedAudience {
  return GUIDED_AUDIENCE_VALUES.includes(v as GuidedAudience)
}

/** Validate a persisted toggle-state blob into the literal-typed shape the
 * toggle buttons use, dropping anything unrecognized rather than passing it
 * through to a `ToggleButtonGroup` that doesn't know the value. */
export function sanitizeGuidedToggles(raw?: PersistedGuidedToggles | null): GuidedToggleState {
  if (!raw) return {}
  const out: GuidedToggleState = {}
  if (isGuidedFormat(raw.format)) out.format = raw.format
  if (isGuidedLength(raw.length)) out.length = raw.length
  if (isGuidedAudience(raw.audience)) out.audience = raw.audience
  return out
}

/** Reverse of `buildGuidanceSentence`: best-effort parse of a trailing
 * guidance sentence back into toggle state, for widgets saved before the
 * structured fields existed. Matches on the exact fixed phrases the builder
 * produces — never throws, returns `{}` when nothing recognizable is found. */
export function parseGuidanceSentence(instructions: string): GuidedToggleState {
  const idx = instructions.indexOf(GUIDANCE_MARKER)
  if (idx === -1) return {}
  const sentence = instructions.slice(idx + GUIDANCE_MARKER.length)
  const toggles: GuidedToggleState = {}

  if (sentence.includes('Present as bullet points.')) toggles.format = 'bullets'
  else if (sentence.includes('Write as flowing prose.')) toggles.format = 'prose'

  for (const key of GUIDED_LENGTH_VALUES) {
    if (sentence.includes(`Aim for roughly ${GUIDED_LENGTH_TEXT[key]}`)) {
      toggles.length = key
      break
    }
  }

  for (const key of GUIDED_AUDIENCE_VALUES) {
    if (sentence.includes(`Write for ${GUIDED_AUDIENCE_TEXT[key]}.`)) {
      toggles.audience = key
      break
    }
  }

  return toggles
}

/** Derive the guided-toggle UI state to show when (re)opening an instructions
 * field: prefer the structured fields persisted alongside the text; fall back
 * to a best-effort parse of the trailing guidance sentence for widgets that
 * predate the structured fields; otherwise show defaults. Never clobbers the
 * instructions text either way — this only decides what the toggle buttons
 * show. */
export function deriveGuidedToggles(instructions: string, structured?: PersistedGuidedToggles | null): GuidedToggleState {
  const sanitized = sanitizeGuidedToggles(structured)
  if (sanitized.format || sanitized.length || sanitized.audience) return sanitized
  return parseGuidanceSentence(instructions)
}

// ---------------------------------------------------------------------------
// Step 2 — @ mention picker over the sources selected in Step 1. Inserts an
// inline `[@Label]` chip token at the cursor, matching the citation-style
// syntax already used for AI-generated `[Table: ...]` report citations.
// ---------------------------------------------------------------------------

export interface MentionMatch {
  /** Text typed after the triggering "@", used to filter the picker list. */
  query: string
  /** Index of the "@" character. */
  start: number
  /** Cursor position (== end of the typed query). */
  end: number
}

/** Detect an in-progress "@mention" ending exactly at `cursorPos`, or null if
 * the cursor isn't inside one (no "@" found before it on the current line, or
 * the run since the last "@" already contains a newline/another "@"). */
export function detectMentionQuery(text: string, cursorPos: number): MentionMatch | null {
  const upToCursor = text.slice(0, cursorPos)
  const at = upToCursor.lastIndexOf('@')
  if (at === -1) return null
  const between = upToCursor.slice(at + 1)
  if (/[\n@]/.test(between)) return null
  return { query: between, start: at, end: cursorPos }
}

/** Replace the active "@query" span with an inline `[@Label] ` token, returning
 * the new text and where the cursor should land afterwards. */
export function insertMention(text: string, start: number, end: number, label: string): { text: string; cursorPos: number } {
  const insertion = `[@${label}] `
  const newText = text.slice(0, start) + insertion + text.slice(end)
  return { text: newText, cursorPos: start + insertion.length }
}

/** Filter the Step-1 source labels available to the mention picker by the typed query. */
export function filterMentionSources(sources: string[], query: string): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return sources
  return sources.filter(s => s.toLowerCase().includes(q))
}

// ---------------------------------------------------------------------------
// Step 2 — pre-filled editable example scaffold. Not placeholder text: the
// field is populated with this so the user edits a realistic example rather
// than facing a blank box (see issue #970 Part A, Step 2).
//
// #1113: deliberately names NO table. analysis_instructions is a per-table
// template — the same string runs against every selected table — so a [@...]
// mention of the first table misdirects the model on all the others.
// ---------------------------------------------------------------------------

export function buildExampleScaffold(): string {
  return 'Summarise the key trends over the last 3 months. ' +
    'Focus on brand movements versus the previous period. ' +
    'Present as bullet points, keeping to approximately 150 words.'
}

// ---------------------------------------------------------------------------
// Part C — missing table/file reference detection for the widget display.
// ---------------------------------------------------------------------------

export interface ConfiguredSourceRef {
  displayName: string
  fullPath: string
  job?: string
}

/** Build the set of table refs the widget is currently configured to use. */
export function configuredTableRefs(tables: MultiTableConfigTable[]): ConfiguredSourceRef[] {
  return tables.map(t => ({
    displayName: t.displayName || tableFullPath(t),
    fullPath: tableFullPath(t),
    job: t.job,
  }))
}

/** Refs from `configured` whose `fullPath` is not present in `validPaths` — renamed,
 * deleted, or moved out from under the widget since it was configured (#970 Part C).
 * `validPaths` (from `tocTablePaths`) is extension-normalized, so the comparison
 * normalizes `fullPath` the same way rather than requiring an exact `.cbt` match. */
export function findMissingTableRefs(configured: ConfiguredSourceRef[], validPaths: Set<string>): ConfiguredSourceRef[] {
  return configured.filter(t => !validPaths.has(normalizeTablePath(t.fullPath)))
}

// ---------------------------------------------------------------------------
// #1047 — extend "settings changed since regenerated" detection to source
// table changes. Order-insensitive: reordering the same tables in Step 1
// isn't a settings change, only adding or removing one is.
// ---------------------------------------------------------------------------

/** True when `current` (the widget's presently-configured table paths) differs
 * from `analyzed` (the table paths the completed analysis actually ran with)
 * as a SET — adding or removing a table trips it, reordering does not. */
export function sourceTablePathsChanged(current: string[], analyzed: string[]): boolean {
  // Legacy analyses (and some API responses) record no table paths at all.
  // An empty analyzed side means "we cannot compare", not "everything was
  // removed" — treating it as a change put a false "Settings changed" banner
  // on every legacy Gen AI card.
  const analyzedKnown = analyzed.filter(Boolean)
  if (analyzedKnown.length === 0) return false
  if (current.length !== analyzedKnown.length) return true
  const analyzedSet = new Set(analyzedKnown)
  return current.some(p => !analyzedSet.has(p))
}

// ---------------------------------------------------------------------------
// #1169 — the analysis start handler resolves {{tables}}, {{month}} and
// {{date}} into the instructions BEFORE snapshotting them onto the analysis
// config (goapi/pkg/server/multitable_analysis.go), while the widget's cell
// still holds the raw templated text. Comparing raw-vs-resolved is permanently
// unequal, which pinned the "Settings changed — data not yet regenerated"
// banner on every templated card. The comparison therefore resolves BOTH sides
// with the analysis's own context (the tables it ran with, the month it ran
// in) before comparing. Worker-side variables ({{tableName}}, {{latestMonth}},
// …) are never resolved into the snapshot, so they compare raw-vs-raw and
// need no handling here.
// ---------------------------------------------------------------------------

export interface SettingsTemplateContext {
  /** Display names of the tables the analysis ran with, in run order. */
  tableNames: string[]
  /** Epoch ms of the analysis run (`created_at`) — the moment the server
   *  resolved {{month}}/{{date}} with its own clock. Falsy = don't resolve. */
  generatedAt?: number
}

const EN_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Replay the server's start-time template substitutions on `text`. Mirrors
 * the server exactly: {{tables}} is only substituted when the table list is
 * non-empty, month names are Go's English names, and any other token passes
 * through untouched. */
export function resolveSettingsTemplate(text: string, ctx: SettingsTemplateContext): string {
  if (!text) return ''
  let result = text
  if (ctx.tableNames.length > 0) {
    result = result.split('{{tables}}').join(ctx.tableNames.join(', '))
  }
  if (ctx.generatedAt) {
    const when = new Date(ctx.generatedAt)
    const month = EN_MONTHS[when.getMonth()]
    result = result.split('{{month}}').join(month)
    result = result.split('{{date}}').join(`${month} ${when.getFullYear()}`)
  }
  return result
}

/** True when the widget cell's text and the analysis snapshot's text differ
 * once both are resolved with the same context — i.e. the settings genuinely
 * changed since the run, rather than merely being stored in resolved form. */
export function templatedSettingsDiffer(cell: string | undefined, snapshot: string | undefined, ctx: SettingsTemplateContext): boolean {
  return resolveSettingsTemplate(cell || '', ctx) !== resolveSettingsTemplate(snapshot || '', ctx)
}
